<template>
    <section>
        <div class="mt-4">
            <div class="flex mr-4 text-blue cursor-pointer" @click="downloadTemplate">
                <b-icon pack="fas" icon="file-excel"></b-icon>
                {{ $t('download_template') }}
            </div>

          <!-- modal descarga de plantilla -->
          <b-modal v-model="downloadModal" :width="300">
            <div class="flex flex-col bg-white p-4 rounded-lg" >
              <div class="flex items center">
                <span class="text-blue md:text-lg font-bold">{{$t('select_template')}}</span>
              </div>
              <div class="border-b border-gray-300 mt-4"></div>
              <div class="flex flex-col mt-4" v-for="(file, index) in files" :key="index">
                <div class="flex items-center cursor-pointer mt-2"
                  @click="downloadTemplateByName(file.filename)">
                  <b-icon pack="fas" icon="file-excel" class="mr-2 text-blue"></b-icon>
                  <span class="text-blue"
                  >{{ $t(file.name) }}</span>
                </div>
              </div>
            </div>
          </b-modal>

            <div class="panel">
                <form @submit.prevent="submitForm" :files="true">
                    <div class="flex w-full">
                        <div class="flex">
                            <b-field class="flex mr-4">
                                <b-upload v-model="form.excelFile" @change="cleanErrors" required
                                    :accept="uploadAccept">
                                    <a class="button is-primary">
                                        <b-icon pack="fas" icon="upload"></b-icon>
                                        <span>{{ $t('select_file') }}</span>
                                    </a>
                                </b-upload>
                                <span class="file-name" v-if="form.excelFile">{{ form.excelFile.name }}</span>
                            </b-field>
                        </div>
                        <div class="flex" >
                            <b-field class="mr-2">
                                <b-select v-model="form.type" size="is-small" rounded icon="file"
                                    :required="true" :disabled="typeLocked" @input="resetValidation()">
                                    <option v-for="item in types" :value="item.value" :key="entityType+'_'+item.value">{{$t(item.name)}}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <!-- Selector capitulo nivel 1 para recursos descompuestos y simple: solo aparece si el proyecto tiene capitulos tipo tag -->
                        <div class="flex" v-if="showTagChapterSelector">
                            <b-field class="mr-2" :type="tagChapterError ? 'is-danger' : ''" :message="tagChapterError ? $t('required_custom_msg', [$t('chapter_tag')]) : ''">
                                <b-select v-model="form.chapter_id" size="is-small" rounded icon="tags" icon-pack="fas"
                                    :placeholder="$t('select_tag_chapter')" @input="onChapterIdChanged">
                                    <option :value="null">{{ $t('select_tag_chapter') }}</option>
                                    <option v-for="ch in tagChapters" :value="ch.id" :key="'tagch_' + ch.id">{{ ch.label }}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="flex" v-if="overwriteEnabled">
                            <v-switch v-model="form.overwrite" ref="form_overwrite" :label="$t('allow_to_overwrite')"
                                :color="form.overwrite ? 'green' : 'red'" class="mr-1"
                                v-on:checked="(value) => {
                                    form.overwrite = value
                                    resetValidation()
                                }" />
                        </div>
                        <div class="flex ml-4">
                            <slot name="descripcion"></slot>
                        </div>
                    </div>
                    <!-- Primavera P6: aviso de creación automática de grupos y campos requeridos -->
                    <template v-if="form.type === 'primavera_p6'">
                        <div class="flex items-center w-full mt-4 has-text-grey">
                            <b-icon pack="fas" icon="info-circle" size="is-small" class="mr-2"></b-icon>
                            <span class="text-sm">{{ $t('p6_groups_auto_create_info') }}</span>
                        </div>
                    </template>

                    <div class="flex w-full mt-4">
                        <div class="flex w-full">
                            <b-field class="w-full">
                                <b-input required :placeholder="$t('description')" v-model="form.description"
                                    maxlength="255" expanded :disabled="!!presetDescription"></b-input>
                            </b-field>
                        </div>
                        <!-- SIMPLE CON BOQ: selector de la cabecera BOQ (una sola), al
                             lado de descripción. Su código se usará luego (al importar)
                             para resolver los precios desde boq_detail. -->
                        <div class="flex w-full ml-4" v-if="form.type == 'resources_bcs_boq'">
                            <b-field class="w-full" :type="boqError ? 'is-danger' : ''"
                                :message="boqError ? $t('select_boq_first') : ''">
                                <multiselect
                                    v-model="selectedBoq"
                                    :options="boqs"
                                    label="name"
                                    track-by="id"
                                    :show-labels="false"
                                    :searchable="true"
                                    :allow-empty="true"
                                    :placeholder="$t('select_boq')"
                                    :loading="isLoadingBoqs">
                                    <template slot="singleLabel" slot-scope="{ option }">
                                        <span class="boq-opt">
                                            <span class="boq-opt__name">{{ option.name }}</span>
                                            <b-tag class="boq-opt__tag" :type="boqTagType(option.type)" size="is-small">{{ $t(boqTypeShortKey(option.type)) }}</b-tag>
                                        </span>
                                    </template>
                                    <template slot="option" slot-scope="{ option }">
                                        <span class="boq-opt">
                                            <span class="boq-opt__name">{{ option.name }}</span>
                                            <b-tag class="boq-opt__tag" :type="boqTagType(option.type)" size="is-small">{{ $t(boqTypeShortKey(option.type)) }}</b-tag>
                                        </span>
                                    </template>
                                    <template slot="noResult">{{ $t('no_elements_found') }}</template>
                                    <template slot="noOptions">{{ $t('no_options_available') }}</template>
                                </multiselect>
                            </b-field>
                        </div>
                        <!-- CARGA BOQ CIVIL: selector de la orden de pedido asociada
                             (obligatorio), solo cuando el tipo es civil. -->
                        <div class="flex w-full ml-4" v-if="entityType === 'boq_detail' && form.type === 'civil'">
                            <b-field class="w-full" :type="workOrderError ? 'is-danger' : ''"
                                :message="workOrderError ? $t('select_work_order') : ''">
                                <multiselect
                                    v-model="selectedWorkOrder"
                                    :options="workOrders"
                                    :custom-label="workOrderLabel"
                                    track-by="id"
                                    :show-labels="false"
                                    :searchable="true"
                                    :allow-empty="true"
                                    :disabled="workOrderLocked"
                                    :placeholder="$t('select_work_order')"
                                    :loading="isLoadingWorkOrders">
                                    <template slot="singleLabel" slot-scope="{ option }">
                                        <span class="wo-opt">
                                            <span class="wo-opt__text">{{ option.code }}<template v-if="option.description"> · {{ option.description }}</template></span>
                                            <b-tag v-if="option.partners && option.partners.length" class="wo-opt__tag" rounded size="is-small">{{ option.partners[0].name }}</b-tag>
                                        </span>
                                    </template>
                                    <template slot="option" slot-scope="{ option }">
                                        <span class="wo-opt">
                                            <span class="wo-opt__text">{{ option.code }}<template v-if="option.description"> · {{ option.description }}</template></span>
                                            <b-tag v-if="option.partners && option.partners.length" class="wo-opt__tag" rounded size="is-small">{{ option.partners[0].name }}</b-tag>
                                        </span>
                                    </template>
                                    <template slot="noResult">{{ $t('no_elements_found') }}</template>
                                    <template slot="noOptions">{{ $t('no_options_available') }}</template>
                                </multiselect>
                            </b-field>
                        </div>
                    </div>

                    <template v-if="form.type === 'primavera_p6'">
                        <div class="flex w-full mt-4 mb-4">
                            <div class="flex w-full mr-4">
                                <b-field class="w-full">
                                    <b-select v-model="form.p6EmailManager" :placeholder="$t('email_manager')"
                                        required expanded>
                                        <option v-for="user in p6ManagersList" :key="user.email" :value="user.email">
                                            {{ user.fullname }} ({{ user.email }})
                                        </option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="flex w-full">
                                <b-field class="w-full">
                                    <b-select v-model="form.p6LocationReference" :placeholder="$t('location_reference_code')"
                                        required expanded>
                                        <option v-for="loc in p6LocationsList" :key="loc.id" :value="loc.reference_code">
                                            {{ loc.reference_code }}
                                        </option>
                                    </b-select>
                                </b-field>
                            </div>
                        </div>
                        <div class="flex w-full mb-4">
                            <div class="flex w-full">
                                <b-field class="w-full">
                                    <b-input v-model="form.p6CodePrefix" :placeholder="$t('p6_code_prefix_placeholder')"
                                        expanded></b-input>
                                </b-field>
                            </div>
                        </div>
                    </template>

                    <!-- fragmento provisional. Lotes canal cargas anteriores -->
                    <div class="flex w-full mt-4 mb-0" v-if="form.type == 'task'">
                        <div class="flex w-full">
                            (Fecha efectiva: Solo admin Epc-tracker)
                        </div>
                        <div class="flex w-full">
                            <b-field class="w-full">
                                <b-datepicker icon="calendar" icon-pack="fas" type="month" v-model="effectiveDate" :placeholder="'fecha efectiva'"
                                    :month-names="$t('months_names')" :day-names="$t('days_names')"
                                    :first-day-of-week="parseInt($t('first_day_of_week'))" :readonly="false" :max-date="previousMonth">
                                </b-datepicker>
                            </b-field>
                        </div>
                    </div>
                    <!-- fin fragmento provisional -->

                    <!-- IMPORTANDO MEDICIONES. Linea para busqueda actividad y fecha efectiva -->
                    <div class="flex w-full mt-4 mb-0"
                        v-if="form.type == 'certifiers' || form.type == 'chapters' || form.type == 'certifiers-chapters'">
                        <div class="flex w-full">
                            <b-field class="w-full">
                                <b-autocomplete required :placeholder="$t('planning_code') + ' ' + $t('activity')"
                                v-model="form.activityCode" minlength="3" maxlength="255" expanded :disabled="disableActivityCode"
                                :data="filteredActivities" @input="getFilteredActivities">
                                </b-autocomplete>
                            </b-field>
                        </div>
                        <div class="flex w-full ml-4">
                            <b-field class="w-full">
                                <b-datepicker icon="calendar" icon-pack="fas" type="month" v-model="effectiveDate" :placeholder="'fecha efectiva'"
                                    :month-names="$t('months_names')" :day-names="$t('days_names')"
                                    :first-day-of-week="parseInt($t('first_day_of_week'))" :readonly="false" :max-date="previousMonth">
                                </b-datepicker>
                            </b-field>
                        </div>
                    </div>


                    <!-- import buttons -->
                    <div class="flex w-full" v-if="entityType == 'assignment' && (form.type != 'resources_bcs' && form.type != 'resources_bcs_boq' && form.type != 'resources_breakdown' && form.type != 'task_subtask')">
                        <div class="flex mr-2">
                            <v-button :loading="isLoading" @click="submit()" type="button" color="green" icon="file-import" icon-pack="fas">{{
                                $t('import') }}</v-button>
                        </div>
                    </div>
                    <div class="flex w-full" v-else>
                        <div class="flex mr-2">
                            <v-button :loading="isLoadingXLSX" type="submit" color="green" icon="upload" icon-pack="fas">{{
                                $t('load_xlsx') }}</v-button>
                        </div>
                        <div v-if="!isValidated" class="flex">
                            <v-button :loading="isLoading" :disabled="!xlsxLoaded" @click="validate()" type="button" color="green" icon="check" icon-pack="fas">{{
                                $t('validate') }}</v-button>
                        </div>
                        <div v-else class="flex">
                            <v-button :loading="isLoading" :disabled="!xlsxLoaded" @click="submit()" type="button" color="green" icon="file-import" icon-pack="fas">{{
                                $t('import') }}</v-button>
                        </div>
                    </div>

                    <!-- progress bar -->
                    <div class="progress-bar-container" v-if="inProgress">
                        <div class="progress-bar" :style="{ width: `${progress}%` }"></div>
                    </div>
                </form>
            </div>
            <div>
                <b-tabs type="is-boxed" v-model="importActiveTab">
                    <b-tab-item>
                        <template slot="header">
                            <b-icon pack="fas" icon="box-open"></b-icon>
                            <span>{{ $t('imports_archive') }}</span>
                        </template>
                        <div>
                            <entity-imports-archive v-if="withHistory" :project-id="projectId" :partner-id="partnerId"
                                :company-id="companyId" :entity-type="entityType" :overwrite-field="true"
                                :type="form.historyType" :action-type="actionType"
                                ref="entity_imports"></entity-imports-archive>
                        </div>
                    </b-tab-item>
                    <!-- Agregando nuevo tag que servira para poder ver el excel en una tabla  -->
                    <b-tab-item :disabled="excelTabEnabled">
                        <template slot="header">
                            <div v-if="entityType == 'assignment' && (form.type != 'resources_bcs' && form.type != 'resources_bcs_boq' && form.type != 'resources_breakdown' && form.type != 'task_subtask')">
                                <b-icon pack="fas" icon="exclamation-circle"></b-icon>
                                <span>
                                    {{ $t('error_log') }}
                                    <b-tag rounded>{{ errorsCount }}</b-tag>
                                </span>
                            </div>
                            <div v-else>
                                <b-icon pack="fas" icon="eye"></b-icon>
                                <span> {{ $t('excel_preview') }} </span>
                            </div>
                        </template>
                        <div v-if="entityType == 'assignment' && (form.type != 'resources_bcs' && form.type != 'resources_bcs_boq' && form.type != 'resources_breakdown' && form.type != 'task_subtask')">
                            <div class="flex items-center font-bold text-red-dark text-ml mb-4">
                                <span>{{ $t('error_import_title') }}</span>
                            </div>
                            <div v-for="(error, index) in errors" :key="'error_' + index">
                                <ul>
                                <li v-for="(msg, index) in error.messages" :key="'errorli_' + index" class="menu-header mb-2">
                                    <div class="flex items-center">
                                    <div class="text-red-dark text-ml">
                                        <span>
                                        {{ $t('import_row_title', [error.row]) }}.
                                        {{ msg }}
                                        </span>
                                    </div>
                                    </div>
                                </li>
                                </ul>
                            </div>
                        </div>
                        <div v-else>
                            <import-excel-preview ref="previewDataExcel" :file="form.excelFile"
                                :overwrite="!!form.overwrite" :projectId="projectId" :entityType="entityType" :type="form.type"
                                :p6CodePrefix="form.p6CodePrefix"
                                @row-changed="rowChanged()"></import-excel-preview>
                        </div>
                    </b-tab-item>
                </b-tabs>
            </div>
        </div>
    </section>
</template>

<script>
import Form from "vform";
import entityImportsArchive from "./entity-imports-archive";
import ImportExcelPreview from '../components/import-excel-preview'
import axios from 'axios';
import * as XLSX from 'xlsx';
import {mapGetters} from 'vuex'
import moment from "moment";
import Multiselect from 'vue-multiselect'
import { uploadFile } from './activity-groups/utils/uppyFactory';

export default {
    name: "import-entities",
    components: {
        entityImportsArchive,
        ImportExcelPreview,
        Multiselect,
    },

    props: {
        withHistory: { type: Boolean, default: true },
        projectId: { type: Number, default: null, required: false },
        partnerId: { type: Number, default: null, required: false },
        companyId: { type: Number, default: null, required: false },
        entityId: { type: String, default: '', required: false }, // mediciones certificadoras van sobre una actividad concreta
        entityType: { type: String, required: true },
        canOverwrite: { type: Boolean, default: false },
        actionType: { type: String, default: '' }, // distinciones a tener en cuenta por el importador
        morphType: { type: String, default: '' }, // usado para cuando se tenga que trabajar con importaciones polimorphicas
        assignmentType: { type: String, default: null }, // para cargar certificadoras se requiere que por defecto sea seleccionado
        extraPayload: { type: Object, default: () => ({}) }, // payload extra (p.ej. parent_id para imports anidados)
        presetDescription: { type: String, default: '' }, // BOQ update: descripción fija (deshabilitada)
        forceUpdate: { type: Boolean, default: false }, // BOQ update: fuerza with_update
        presetType: { type: String, default: '' }, // BOQ update: tipo fijo (bloqueado)
        presetWorkOrderId: { type: Number, default: null }, // BOQ update civil: orden fija (bloqueada)
    },

    created() {
        // si pasamos un codigo actividad, para importar cantidades ejecutadas sobre una medicion (desde la modal)
        if (this.entityId !== '') {
            this.form.activityCode = this.entityId
            this.form.overwrite = 1 // para cargar cantidades ejecutadas se requiere esta opcion a true
            this.form.assignmentType = this.assignmentType
            this.disableActivityCode = true,
            this.form.historyType = this.assignmentType

        }
        // BOQ update: descripción fija (deshabilitada) + modo actualización.
        if (this.presetDescription) this.form.description = this.presetDescription
        if (this.forceUpdate) this.form.overwrite = 1
    },

    mounted() {
        this.getTemplates()
        this.getTypes()
        // BOQ update: pre-seleccionar (y bloquear) el tipo; si es civil, también la
        // orden de pedido asociada. getTypes() dejó form.type en null, lo forzamos.
        if (this.presetType) {
            this.form.type = this.presetType
        }
        if (this.presetWorkOrderId != null) {
            this.form.work_order_id = this.presetWorkOrderId
        }
        this.$nextTick(() => {
            this.loadImportStatus()
        })
        this.form.assignmentType = this.form.type
    },

    watch: {
        importEntities: {
            handler(newValue, oldValue) {
                this.loadImportStatus();
            },
            deep: true
        },

        // BOQ update: al entrar/salir del modo actualización desde manage-boqs.
        presetDescription(v) {
            this.form.description = v || '';
        },
        forceUpdate(v) {
            if (v) this.form.overwrite = 1;
        },

        'form.assignmentType': function (newVal) {
            this.form.historyType = this.form.assignmentType
        },

        'form.type': function (newVal) {
            // al cambiar a recursos descompuestos o simple plano cargamos los capitulos tag del proyecto
            if ((newVal === 'resources_breakdown' || newVal === 'resources_bcs') && this.entityType === 'assignment' && this.projectId) {
                this.loadTagChapters();
            } else {
                this.tagChapters = [];
                this.form.chapter_id = null;
                this.tagChapterError = false;
            }

            // Simple con BOQ: al elegir el tipo cargamos las cabeceras boqs; al
            // salir limpiamos la selección.
            if (newVal === 'resources_bcs_boq' && this.entityType === 'assignment') {
                this.loadBoqs();
            } else {
                this.boqs = [];
                this.form.boq_id = null;
            }
            this.boqError = false;

            // Carga BOQ civil: al elegir civil cargamos las órdenes de pedido; al
            // salir limpiamos la selección.
            if (newVal === 'civil' && this.entityType === 'boq_detail') {
                this.loadWorkOrders();
            } else {
                this.workOrders = [];
                this.form.work_order_id = null;
            }
            this.workOrderError = false;
        },

        isBusy: {
            immediate: true,
            handler(busy) {
                this.$emit('busy', busy);
            },
        },
    },

    data: () => ({
        isLoading: false,
        form: new Form({
            projectId: null,
            partnerId: null,
            companyId: null,
            excelFile: null,
            description: "",
            overwrite: false,
            assignmentType: null,
            activityCode: null,
            actionType: "",
            workType: null,
            effectiveDate: "",
            type: "",
            reset: false,
            historyType: null,
            p6EmailManager: null,
            p6LocationReference: null,
            p6CodePrefix: '',
            chapter_id: null,
            // Simple con BOQ: cabecera boqs elegida (una sola).
            boq_id: null,
            // Carga BOQ civil: orden de pedido asociada.
            work_order_id: null,
        }),
        effectiveDate: null,
        errors: [],
        importActiveTab: 0,
        errorsCount: 0,
        disableActivityCode: false,
        filteredActivities: [],
        excelTabEnabled: true,
        isValidated: false,
        xlsxLoaded: false,
        isLoadingXLSX: false,
        files: [],
        downloadModal: false,
        types: [],
        chunkSize: 500,
        inProgress: false,
        progress: 0,
        timeout: null,
        p6ManagersList: [],
        p6LocationsList: [],
        tagChapters: [],
        tagChapterError: false,
        // Simple con BOQ: lista de cabeceras boqs disponibles del proyecto.
        boqs: [],
        isLoadingBoqs: false,
        boqError: false,
        // Carga BOQ civil: órdenes de pedido del proyecto.
        workOrders: [],
        isLoadingWorkOrders: false,
        workOrderError: false,
    }),

    computed: {
        ...mapGetters({
            importEntities: 'app/importEntities',
            company: 'app/company',
        }),
        templateName() {
            // TODO: de momento hacemos este hack para mantener funcionando (replace('/', '-')) lo ideal es quitar el replace y mantener una convencion para entitytype
            const filename = "template_import_" + this.entityType.replace('/', '-') + ".xlsx";
            // Override local para boq_detail: el layout default `app.vue` tiene un bug
            // en `assetVapor` que produce '//path' cuando ASSET_PATH='/' en local
            // (protocol-relative URL → el browser no lo resuelve). Devolvemos ruta
            // relativa directa; el archivo se sirve desde /public/. En Vapor prod
            // el archivo se moverá a /dist/ y este case se puede revisar entonces.
            if (this.entityType === 'boq_detail' || this.entityType === 'takeoff') {
                return '/' + filename;
            }
            return this.assetVapor(filename);
        },
        templateFileName() {
            // TODO: de momento hacemos este hack para mantener funcionando (replace('/', '-'))
            return this.$t(this.entityType.replace('/', '-') + "_import_template") + ".xlsx";
        },
        overwriteEnabled() {
            return this.canOverwrite;
        },
        // Para P6 aceptamos archivos .xer, para el resto archivos Excel
        uploadAccept() {
            return this.form.type === 'primavera_p6'
                ? '.xer'
                : 'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        },
        // Para P6 enviamos 'prelations' al backend porque usamos la misma estructura
        backendType() {
            return this.form.type === 'primavera_p6' ? 'prelations' : this.form.type;
        },
        // Indicamos al backend si la fuente es Primavera P6 para cálculos post-importación
        importSource() {
            return this.form.type === 'primavera_p6' ? 'primavera_p6' : null;
        },
        previousMonth() {
            let today = new Date()
            return new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
        },
        // Mostramos el selector de capitulo tag en recursos descompuestos y simple plano, solo si el proyecto tiene capitulos de ese tipo
        showTagChapterSelector() {
            return this.entityType === 'assignment'
                && (this.form.type === 'resources_breakdown' || this.form.type === 'resources_bcs')
                && this.tagChapters.length > 0;
        },
        isBusy() {
            return this.isLoading || this.isLoadingXLSX || this.inProgress;
        },
        // BOQ update: tipo bloqueado (viene fijo del BOQ que se actualiza).
        typeLocked() {
            return !!this.presetType;
        },
        // BOQ update civil: orden de pedido bloqueada.
        workOrderLocked() {
            return !!this.presetType && this.form.type === 'civil';
        },
        // Simple con BOQ: proxy entre el objeto del multiselect y form.boq_id.
        selectedBoq: {
            get() {
                return this.boqs.find(b => Number(b.id) === Number(this.form.boq_id)) || null;
            },
            set(value) {
                this.form.boq_id = value ? value.id : null;
                if (value) this.boqError = false;
            },
        },
        // Carga BOQ civil: proxy entre el objeto del multiselect y form.work_order_id.
        selectedWorkOrder: {
            get() {
                return this.workOrders.find(o => Number(o.id) === Number(this.form.work_order_id)) || null;
            },
            set(value) {
                this.form.work_order_id = value ? value.id : null;
                if (value) this.workOrderError = false;
            },
        },
    },

    methods: {
        async submitForm() {
            this.importActiveTab = 1;
            this.excelTabEnabled = false;
            this.isLoadingXLSX = true;
            this.xlsxLoaded = false;
            this.isValidated = false;
            this.form.reset = true;
            try {
                let response = await this.$refs.previewDataExcel.processFile();
                this.isLoadingXLSX = false;

                if (response.success) {
                    this.xlsxLoaded = true;
                } else {
                    this.$notify.error("error_import");
                }
            } catch (error) {
                this.isLoadingXLSX = false;
                this.$notify.error("error_import");
            }
        },

        cleanErrors() {
            this.importActiveTab = 0;
            this.excelTabEnabled = true;
            this.form.descripcion = '';
            if (this.$refs.previewDataExcel?.previewData) {
                this.$refs.previewDataExcel.previewData = [];
            }
        },


        downloadTemplateByName(filename) {
            let link = document.createElement("a");
            link.href = this.assetVapor(filename);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        },

        downloadTemplate() {
            if (this.files.length) {
                this.downloadModal = true;
                return;
            }
            let link = document.createElement("a");
            link.href = this.templateName;
            link.download = this.templateFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        async validate() {
            // Recursos descompuestos y simple plano: si el proyecto tiene capitulos tag debemos exigir nivel 1 antes de validar
            if (!this.ensureTagChapterSelected()) {
                return;
            }
            // Simple con BOQ: la cabecera BOQ es obligatoria.
            if (!this.ensureBoqSelected()) {
                return;
            }
            // Carga BOQ civil: la orden de pedido es obligatoria.
            if (!this.ensureWorkOrderSelected()) {
                return;
            }

            this.isLoading = true
            try {
                // Si es P6, inyectamos los campos requeridos y creamos los grupos
                if (this.form.type === 'primavera_p6') {
                    this.injectP6RequiredFields();
                    await this.ensureP6Groups();
                }

                let validatedData = []
                let mappedData = this.mapFields(this.$refs.previewDataExcel.previewData, this.$refs.previewDataExcel.getFields())
                let hasErrors = false

                for (let i = 0; i < mappedData.length; i += this.chunkSize) {
                    let chunk = mappedData.slice(i, i + this.chunkSize);
                    let { data } = await axios.post(`/api/v2/import/${this.entityType}/optimized/validate`, {
                        data: chunk,
                        project_id: this.projectId,
                        with_update: this.form.overwrite,
                        description: this.form.description,
                        is_last: (i + this.chunkSize) >= mappedData.length,
                        ...this.form,
                        ...this.extraPayload,
                        type: this.backendType,
                        source: this.importSource,
                        reset: i == 0,
                    });

                    if ((data.success == false)) {
                        hasErrors = true;
                    }
                    // P6 y takeoff: cada chunk vuelve validado; el resto solo el último trae todo.
                    if (this.form.type === 'primavera_p6' || this.entityType === 'takeoff') {
                        validatedData = validatedData.concat(data.data || [])
                    } else {
                        validatedData = data.data
                    }
                }

                this.$refs.previewDataExcel.previewData = this.mapFields(validatedData, this.$refs.previewDataExcel.getFields(), false);
                if (hasErrors) {
                    this.$notify.error("error_import_validate");
                } else {
                    this.$notify.success("success_import_validate");
                    this.isValidated = true;
                }
            } catch (error) {
                console.log(error)
                this.$notify.error("error_import_validate");
            }
            this.isLoading = false
        },

        async submit() {

            // Misma guardia que en validate: si hay capitulos tag, el nivel 1 es obligatorio antes de importar
            if (!this.ensureTagChapterSelected()) {
                return;
            }
            // Simple con BOQ: la cabecera BOQ es obligatoria.
            if (!this.ensureBoqSelected()) {
                return;
            }
            // Carga BOQ civil: la orden de pedido es obligatoria.
            if (!this.ensureWorkOrderSelected()) {
                return;
            }

            // Importamos el calendario P6 justo antes de la importación de actividades
            if (this.form.type === 'primavera_p6') {
                await this.importP6Calendar();
            }

            // Fast path para entities con backend síncrono (no dispatchan Job ni
            // esperan Pusher). Enum explícito para no acoplar comportamiento
            // globalmente — nuevas entities sync se añaden aquí.
            const SYNC_ENTITIES = ['boq_detail', 'takeoff']
            if (SYNC_ENTITIES.includes(this.entityType)) {
                await this.importDataSync()
                return
            }

            if (
                this.entityType == 'assignment' &&
                (
                    this.form.type != 'resources_bcs' &&
                    this.form.type != 'resources_bcs_boq' &&
                    this.form.type != 'resources_breakdown' &&
                    this.form.type != 'task_subtask'
                )
            ) {
                await this.importData()
            } else {
                await this.importDataOptimized()
            }

        },

        // Import síncrono: el backend inserta en la misma request y devuelve 200 al
        // terminar. No usamos el store `importEntities` ni esperamos eventos Pusher,
        // así el spinner cierra al momento y emitimos `success` para que el padre
        // recargue su listado.
        async importDataSync() {
            if (!this.isValidated) return
            this.isLoading = true
            this.progress = 0
            this.inProgress = true

            let mappedData = this.mapFields(
                this.$refs.previewDataExcel.previewData,
                this.$refs.previewDataExcel.getFields()
            )

            let awsFile = null
            try {
                // BOQ / takeoff: subir el Excel original a S3 para el histórico.
                if ((this.entityType === 'boq_detail' || this.entityType === 'takeoff') && this.form.excelFile) {
                    try {
                        const { response } = await uploadFile(this.form.excelFile)
                        if (response && response.body && response.body.key) {
                            awsFile = {
                                key: response.body.key,
                                name: this.form.excelFile.name,
                                content_type: this.form.excelFile.type,
                            }
                        }
                    } catch (e) {
                        awsFile = null
                    }
                }

                for (let i = 0; i < mappedData.length; i += this.chunkSize) {
                    let chunk = mappedData.slice(i, i + this.chunkSize)
                    let payload = {
                        data: chunk,
                        project_id: this.projectId,
                        with_update: this.form.overwrite,
                        description: this.form.description,
                        is_last: (i + this.chunkSize) >= mappedData.length,
                        ...this.form,
                        ...this.extraPayload,
                        type: this.backendType,
                        reset: i === 0,
                    }
                    if (awsFile && (this.entityType !== 'takeoff' || payload.is_last)) {
                        payload.file = awsFile
                        payload.original_filename = this.form.excelFile ? this.form.excelFile.name : null
                        if (this.entityType === 'takeoff') {
                            payload.row_count = mappedData.length
                        }
                    }
                    await axios.post(`/api/v2/import/${this.entityType}/optimized`, payload)
                    if (this.entityType === 'takeoff') {
                        this.progress = Math.round(((i + chunk.length) / mappedData.length) * 100)
                    }
                }

                // Reset UI a estado limpio (mismo shape que el else de loadImportStatus).
                this.cleanErrors()
                this.form.excelFile = null
                this.form.description = ''
                this.xlsxLoaded = false
                this.isValidated = false
                this.progress = 0
                this.$notify.success('success_import')
                this.$nextTick(() => {
                    if (this.$refs.entity_imports) {
                        this.$refs.entity_imports.reloadImports()
                    }
                })
                this.$emit('success')
            } catch (error) {
                this.$notify.error('error_import')
            } finally {
                this.isLoading = false
                this.inProgress = false
            }
        },

        // Simple con BOQ: exige elegir una cabecera BOQ antes de validar/importar.
        // Solo aplica a ese tipo; el resto no lo requiere.
        ensureBoqSelected() {
            if (this.entityType === 'assignment' && this.form.type === 'resources_bcs_boq' && !this.form.boq_id) {
                this.boqError = true;
                this.$notify.error('select_boq_first');
                return false;
            }
            this.boqError = false;
            return true;
        },

        ensureTagChapterSelected() {
            if (this.showTagChapterSelector && !this.form.chapter_id) {
                this.tagChapterError = true;
                this.$notify.error('select_tag_chapter');
                return false;
            }
            this.tagChapterError = false;
            return true;
        },

        onChapterIdChanged() {
            this.tagChapterError = false;
            // Si ya hay datos validados, los reseteamos porque el destino cambio
            if (this.isValidated) {
                this.resetValidation();
            }
        },

        // Simple con BOQ: tipo/disciplina del BOQ para el tag del selector (mismo
        // criterio que manage-boqs; numeración unificada BoqType 1=civil, 2=mecánico, 3=eléctrico).
        boqTypeShortKey(type) {
            const n = Number(type);
            if (n === 1) return 'civil';
            if (n === 2) return 'mechanical';
            return 'electrical';
        },
        boqTagType(type) {
            const n = Number(type);
            if (n === 1) return 'is-success';   // civil
            if (n === 2) return 'is-info';      // mecánico
            return 'is-warning';                 // eléctrico
        },

        // Simple con BOQ: carga las cabeceras boqs disponibles del proyecto para
        // el selector (una sola se elige; su código se usará luego al importar
        // para resolver precios desde boq_detail).
        async loadBoqs() {
            if (!this.projectId) return;
            this.isLoadingBoqs = true;
            try {
                const { data } = await axios.get(`/api/v2/project/${this.projectId}/boqs`);
                this.boqs = (data && data.success && data.boqs) ? data.boqs : [];
            } catch (e) {
                this.boqs = [];
            } finally {
                this.isLoadingBoqs = false;
            }
        },

        // Carga BOQ civil: órdenes de pedido del proyecto (con su subcontrata) para el selector.
        async loadWorkOrders() {
            if (!this.projectId) return;
            this.isLoadingWorkOrders = true;
            try {
                const { data } = await axios.get('/api/v2/work-orders', { params: { project_id: this.projectId, with_partners: 1 } });
                this.workOrders = (data && data.success && data.data) ? data.data : [];
            } catch (e) {
                this.workOrders = [];
            } finally {
                this.isLoadingWorkOrders = false;
            }
        },
        // Etiqueta del selector de orden: "code · descripción — subcontrata".
        workOrderLabel(o) {
            if (!o) return '';
            const base = o.description ? `${o.code} · ${o.description}` : `${o.code}`;
            const partner = (o.partners && o.partners.length) ? o.partners[0].name : null;
            return partner ? `${base} — ${partner}` : base;
        },
        // Carga BOQ civil: exige elegir orden de pedido antes de validar/importar.
        ensureWorkOrderSelected() {
            if (this.entityType === 'boq_detail' && this.form.type === 'civil' && !this.form.work_order_id) {
                this.workOrderError = true;
                this.$notify.error('select_work_order');
                return false;
            }
            this.workOrderError = false;
            return true;
        },

        async loadTagChapters() {
            try {
                const { data } = await axios.get(
                    `/api/v2/project/${this.projectId}/chapters`,
                    { params: { template: 'select', type: 2 } }
                );
                if (data && data.success) {
                    // Solo mostramos capitulos tag de nivel 1 (root). Los niveles 2 y 3 se generan automaticamente desde las pestañas y headers del Excel al importar.
                    this.tagChapters = (data.chapters || []).map(node => ({
                        id: node.id,
                        label: node.label || node.name || node.code || `#${node.id}`,
                    }));
                } else {
                    this.tagChapters = [];
                }
            } catch (err) {
                this.tagChapters = [];
            }
        },

        async importDataOptimized() {
            if (this.isValidated) {
                this.isLoading = true
                this.progress = 0
                this.inProgress = true
                this.$refs.previewDataExcel.updateDataStatus(this.$refs.previewDataExcel.previewData.length - 1, 'progress')
                let mappedData = this.mapFields(this.$refs.previewDataExcel.previewData, this.$refs.previewDataExcel.getFields())
                try {
                    // Subimos el XER original a S3 para guardarlo en el historial
                    let xerS3Key = null
                    if (this.form.type === 'primavera_p6' && this.form.excelFile) {
                        try {
                            const xerFile = new File([this.form.excelFile], this.form.excelFile.name, { type: 'application/octet-stream' })
                            const { response } = await uploadFile(xerFile)
                            xerS3Key = response.body.key
                        } catch (err) {
                        }
                    }

                    for (let i = 0; i < mappedData.length; i += this.chunkSize) {
                        let chunk = mappedData.slice(i, i + this.chunkSize);
                        let payload = {
                            data: chunk,
                            project_id: this.projectId,
                            with_update: this.form.overwrite,
                            description: this.form.description,
                            is_last: (i + this.chunkSize) >= mappedData.length,
                            ...this.form,
                            ...this.extraPayload,
                            type: this.backendType,
                            reset: i == 0,
                        };
                        payload.source = this.importSource;
                        if (xerS3Key) payload.xer_s3_key = xerS3Key;
                        let { data } = await axios.post(`/api/v2/import/${this.entityType}/optimized`, payload);
                    }

                    let importEntities = self.importEntities || {}
                    if (!importEntities[this.entityType]) {
                        importEntities[this.entityType] = {}
                    }
                    let chunkProgress = (100 / (Math.ceil(mappedData.length / 50)))
                    // Para datasets grandes (P6) no guardamos la data en el store para no reventar localStorage
                    let storeData = mappedData.length > 5000 ? [] : mappedData
                    importEntities[this.entityType][`${this.projectId}`] = { status: 'pending', data: storeData, type: this.form.type, chunkProgress: chunkProgress, progress: 0, lastNotification: Date.now()}
                    this.$store.dispatch('app/setImportEntities', {importEntities})

                    this.$notify.success("import_in_progress");
                } catch (error) {
                    this.$notify.error("error_import");
                    this.isLoading = false
                    this.inProgress = false
                }
            }
        },

        async importData() {
            this.isLoading = true;
            var self = this;
            this.cleanErrors();

            this.form.projectId = this.projectId;
            this.form.partnerId = this.partnerId;
            this.form.companyId = this.companyId;
            this.form.actionType = this.actionType;
            this.form.effectiveDate = this.effectiveDate
                ? moment(this.effectiveDate).format('YYYY-MM-DD')
                : "";
            if (this.morphType)
                this.form.morphType = this.morphType
            this.form
                .post("/api/v2/import/" + this.entityType)
                .then(function (response) {

                    self.isLoading = false;
                    if (response.data.success) {

                        self.$notify.success("success_import");
                        self.form.description = "";
                        if (self.withHistory)
                        self.$refs.entity_imports.reloadImports();
                        self.$emit("success");
                    } else {

                        self.$notify.error("error_import");

                        // Procesamos los errores para mostrarlos
                        for (let key in response.data.errors) {
                            let error_info = key.split(".");
                            let row = parseInt(error_info[0]);
                            let field = `[${error_info[1]}]`;//self.$t(error_info[1]);

                            let messages = response.data.errors[key].map(function (msg) {
                                return self.$t(msg) !== msg
                                ? self.$t(msg, [field])
                                : msg; // prefiero el msg tal cual se devuelve en el importador
                                // : self.$t("import_generic_error_msg", [parseInt(field)+1]); // mensaje generico sin muchas pistas
                            });

                            self.errorsCount = self.errorsCount + messages.length;

                            self.errors.push({ row: row, field: field, messages: messages });
                        }

                        self.errors = self.errors.sort(function (a, b) {
                            return parseInt(a.row) - parseInt(b.row);
                        });

                        self.importActiveTab = 1;
                    }
                })
                .catch(error => {
                    self.isLoading = false;
                    self.$notify.error("error_import");
                });

            this.form.excelFile = null;
        },

        rowChanged() {
            this.isValidated = false;
        },

        resetValidation() {
            this.isValidated = false
            this.form.assignmentType = this.form.type
            if (this.$refs.previewDataExcel?.previewData) {
                this.$refs.previewDataExcel.updateDataStatus(this.$refs.previewDataExcel.previewData.length - 1, 'start')
            }
            // Cargamos las opciones de managers y locations cuando se selecciona P6
            if (this.form.type === 'primavera_p6' && !this.p6ManagersList.length) {
                this.loadP6Options();
            }
        },

        loadImportStatus() {
            if (!this.importEntities || !this.importEntities[this.entityType]) {
                return true
            }
            let importEntities = this.importEntities[this.entityType][`${this.projectId}`]
            if (this.timeout) {
                clearTimeout(this.timeout)
            }
            if (importEntities && importEntities.status) {
                this.importActiveTab = 1;
                this.excelTabEnabled = true;
                this.xlsxLoaded = true;
                this.$set(this.form, 'type', importEntities.type);
                let errors = importEntities.errors || {}

                // El ref previewDataExcel vive adentro de un b-tab-item: si el tab no se renderizo todavia,
                // no existe. Esperamos al proximo tick despues de activar el tab para evitar el TypeError.
                this.$nextTick(() => {
                    if (!this.$refs.previewDataExcel) {
                        return;
                    }
                    let data = this.mapFields(importEntities.data || [], this.$refs.previewDataExcel.getFields(importEntities.type), false);
                    this.$refs.previewDataExcel.setData(data)

                    if (importEntities.status == 'error') {
                        this.$refs.previewDataExcel.updateDataStatus(this.$refs.previewDataExcel.previewData.length - 1, 'validated')
                        this.$refs.previewDataExcel.updateDataErrors(errors)
                    }
                    if (importEntities.lastImportedIndex) {
                        this.$refs.previewDataExcel.updateDataStatus(importEntities.lastImportedIndex, 'imported')
                    }
                });

                if (importEntities.status == 'error') {
                    this.isLoading = false
                    this.isValidated = false
                } else {
                    this.isLoading = true
                    this.isValidated = true
                    this.inProgress = true
                    this.progress = importEntities.progress
                }
                if (importEntities.status == 'pending') {
                    this.watchImportTime(importEntities)
                }
            } else {
                this.cleanErrors();
                this.form.excelFile = null;
                this.xlsxLoaded = false
                this.isLoading = false
                this.isValidated = false
                this.inProgress = false
                this.progress = 0
                // Guard: el ref sólo existe si withHistory=true (entity-imports-archive se
                // renderiza condicionalmente) y ademas vive adentro de un b-tab-item, por lo
                // que puede no estar montado al inicio: esperamos al proximo tick.
                this.$nextTick(() => {
                    if (this.$refs.entity_imports) {
                        this.$refs.entity_imports.reloadImports();
                    }
                });
            }
        },

        watchImportTime(importEntities) {
            let lastTime = importEntities.lastNotification
            let timeout = lastTime + (10*60*1000)
            let currentTimeout = timeout - Date.now()

            if (currentTimeout <= 0) {
                this.resetImport()
            } else {
                this.timeout = setTimeout(() => {
                    this.resetImport()
                }, currentTimeout);
            }
        },

        resetImport(showError = true) {
            this.cleanErrors();
            this.form.excelFile = null;
            this.xlsxLoaded = false
            this.isLoading = false
            this.isValidated = false
            this.inProgress = false
            this.progress = 0

            let importEntities = this.importEntities || {}
            if (importEntities[this.entityType]) {
                importEntities[this.entityType][`${this.projectId}`] = {}
            }
            this.$store.dispatch('app/setImportEntities', {importEntities})

            if (showError) {
                this.$notify.error('error_import');
            }
        },

        // Primavera P6
        // Cargamos managers y locations del proyecto para los selectores P6
        async loadP6Options() {
            const [managersRes, locsRes] = await Promise.all([
                axios.get(`/api/v2/project/${this.projectId}/users`, { params: { r: 'project_admin' } }),
                axios.get('/api/v2/locations', { params: { p: this.projectId, all: true } })
            ]);

            if (managersRes.data?.success) {
                this.p6ManagersList = managersRes.data.users_list || [];
            }
            if (locsRes.data?.success) {
                this.p6LocationsList = locsRes.data.locations || [];
            }
        },

        // Inyectamos email_manager y location_reference_code en todas las filas cargadas
        injectP6RequiredFields() {
            this.$refs.previewDataExcel.previewData.forEach(row => {
                row.email_manager = this.form.p6EmailManager;
                row.location_reference_code = this.form.p6LocationReference;
            });
        },

        // Auto-creación de grupos (WBS) ---
        // Enviamos los grupos WBS extraídos del XER al backend en una sola llamada.
        async ensureP6Groups() {
            const groups = this.$refs.previewDataExcel.p6Groups || [];
            if (!groups.length) return;

            await axios.post(`/api/v2/project/${this.projectId}/activity/groups/bulk`, {
                groups,
                entity_type: 'project'
            });

            this.$notify.success('groups_created_successfully');
        },

        // Importamos el calendario P6 (work_days + holidays) al proyecto
        async importP6Calendar() {
            const calendar = this.$refs.previewDataExcel.p6Calendar;
            if (!calendar) return;

            await axios.post(`/api/v2/project/${this.projectId}/calendar/import-p6`, calendar);
        },

        async getFilteredActivities() {
            if (this.form.activityCode && this.form.activityCode.length > 2) {
                let getParams = {
                    p: this.projectId,
                    filter_planning_code: this.form.activityCode,
                    light: 1
                }
                const { data } = await axios.get('/api/v2/activities', { params: getParams });
                this.filteredActivities = data.activities.map(activity => activity.planning_code);
            }

            else {
                this.filteredActivities = [];
            }
        },

        mapFields(dataArray, structure, toIndex = true) {
            let baseFields = {
                skip: 0,
                import_status: 1,
                status_error: 2,
                actions: 3,
            };

            let finalStructure = {
                ...baseFields,
                ...Object.fromEntries(
                    Object.entries(structure).map(([key, value]) => [key, value + 4])
                ),
            };

            if (!toIndex) {
                finalStructure = Object.fromEntries(
                        Object.entries(finalStructure).map(([key, value]) => [value, key])
                    )
            }

            return dataArray.map(item => {
                let mappedItem = {};
                for (let [key, index] of Object.entries(finalStructure)) {
                    mappedItem[index] = item[key]
                }
                return mappedItem;
            });
        },

        // TODO: Aqui van metodos que cargan configuracion para cada entidad (esto deberia luego ser movido a alguna interfaz o parecido que maneje esto de manera mas limpia)
        getTemplates() {
            switch (this.entityType) {
                case 'activities':
                    this.files = [
                        { filename:"template_import_activities_simple.xlsx", name:"simple" },
                        { filename:"template_import_activities_prelations.xlsx", name:"with_linked_activities" },
                    ]
                    break;
                case 'assignment':
                    this.files = [
                        { filename:"template_import_recursos_(BC3).xlsx", entityType: "assignment", name:"resources_bc3" },
                        { filename:"template_import_mediciones_Simple.xlsx", entityType: "assignment", name:"simple" },
                        { filename:"template_import_resources_breakdown.xlsx", entityType: "assignment", name:"resources_breakdown" },
                        ...(this.company?.configs?.subtasks ? [{ filename:"template_import_tasks_and_subtasks.xlsx", entityType: "assignment", name:"task_subtask" }] : []),
                    ]
                    break;
                case 'warehouse_packing_list':
                    this.files = [
                        { filename:"template_import_packing_list.xlsx", entityType: "warehouse_packing_list", name:"warehouse_packing_list_import_template" },
                    ]
                    break;
            }
        },

        getTypes() {
            switch (this.entityType) {
                case 'activities':
                    this.types = [
                        {value: null, 'name': 'select'},
                        {value: 'simple', 'name': 'simple'},
                        {value: 'prelations', 'name': 'with_linked_activities'},
                        {value: 'primavera_p6', 'name': 'primavera_p6'}
                    ]
                    this.form.type = null // default value (antes 'simple')
                    break;
                case 'assignment':
                    this.types = [
                        {value: null, 'name': 'select'},
                        {value: 'task', 'name': 'tasks'},
                        ...(this.company?.configs?.subtasks ? [{ value: 'task_subtask', name: 'task_subtask' }] : []),
                        {value: 'resources_bc3', 'name': 'resources_bc3'},
                        {value: 'certifiers', 'name': 'certifiers'},
                        {value: 'resources_bcs', 'name': 'simple'},
                        {value: 'resources_bcs_boq', 'name': 'simple_with_boq'},
                        {value: 'resources_breakdown', 'name': 'resources_breakdown'},
                        {value: 'chapters', 'name': 'chapters'},
                        {value: 'certifiers-chapters', 'name': 'certifiers_chapters'},
                    ]
                    this.form.type = null // default value (antes 'task')
                    break;
                case 'warehouse_packing_list':
                    this.types = [
                        { value: 'simple', name: 'import_packing_list_simple' },
                    ]
                    this.form.type = 'simple'
                    break;
                case 'boq_detail':
                    this.types = [
                        { value: null,         name: 'select' },
                        { value: 'mechanical', name: 'mechanical_installation' },
                        { value: 'electrical', name: 'electrical_installation' },
                        { value: 'civil',      name: 'civil_installation' },
                    ]
                    this.form.type = null
                    break;
                case 'takeoff':
                    this.types = [
                        { value: 'type1', name: 'takeoff_type_1' },
                        { value: 'type2', name: 'takeoff_type_2' },
                    ]
                    this.form.type = 'type1'
                    break;
            }
        }
    }
};
</script>
<style scoped>
.progress-bar-container {
    margin-top: 10px;
    width: 100%;
    height: 7px;
    background-color: #eee;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.progress-bar {
    height: 100%;
    background-color: #54e086;
    transition: width 1s ease;
}

/* Simple con BOQ: opción del multiselect (nombre protagonista + tag de tipo a la derecha) */
.boq-opt {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    width: 100%;
}
.boq-opt__name {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
    color: #33518f;
    line-height: 1.6;
    padding: 1px 0;
}
/* Tag discreto a la derecha: pequeño, tenue, que no compita con el texto */
.boq-opt__tag {
    flex: 0 0 auto;
    opacity: 0.75;
    font-size: 10px;
    height: auto;
    padding: 1px 6px;
}
/* Evita que el texto se corte verticalmente en el valor seleccionado y en las
   opciones del desplegable (el contenedor de vue-multiselect quedaba muy justo). */
::v-deep .multiselect__single,
::v-deep .multiselect__option {
    line-height: 1.6;
    min-height: 1.6em;
    white-space: nowrap;
}
::v-deep .multiselect__single {
    overflow: visible;
    padding-top: 2px;
}

/* Carga BOQ civil: opción del select de orden (texto protagonista + subcontrata gris tenue) */
.wo-opt {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
    width: 100%;
}
.wo-opt__text {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
    color: #33518f;
}
.wo-opt__tag {
    flex: 0 0 auto;
    opacity: 0.9;
    font-size: 10px;
    height: auto;
    padding: 1px 8px;
}
</style>
